import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { Activity, Eye, MousePointerClick, ShieldCheck, TrendingUp, UserRoundCheck } from "lucide-react";

interface TrackingReport {
  summary: {
    sent30d: number;
    opened30d: number;
    clicked30d: number;
    openEvents30d: number;
    clickEvents30d: number;
    unsubscribed30d: number;
    openRatePct: number;
    clickRatePct: number;
    clickToOpenRatePct: number;
    engagementProfiles: number;
  };
  consent: {
    trackingMode: "consent_aware";
    failClosed: boolean;
    source: string;
  };
  deliverability: {
    reputationScore: number | null;
    bounceRatePct: number | null;
    complaintRatePct: number | null;
  };
  profiles: Array<{
    personId: string;
    emailsSent30d: number;
    emailsOpened30d: number;
    emailsClicked30d: number;
    openRatePct: number;
    clickRatePct: number;
    lastEmailSentAt: string | null;
    lastEmailOpenedAt: string | null;
    lastEmailClickedAt: string | null;
    totalRevenue: number;
    engagementScore: number;
  }>;
}

function formatPct(value: number | null): string {
  return value == null ? "N/A" : `${value.toFixed(value % 1 === 0 ? 0 : 1)}%`;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : "Never";
}

export default function EmailTrackingPage() {
  const [data, setData] = useState<TrackingReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    apiFetch<{ data: TrackingReport }>("/email-marketing/tracking")
      .then((response) => {
        if (!isMounted) return;
        setData(response.data);
        setError(null);
      })
      .catch(() => {
        if (!isMounted) return;
        setData(null);
        setError("Failed to load email tracking report");
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-56 rounded" />
        <div className="grid gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonCard key={index} className="h-28 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
        <Activity className="mx-auto h-9 w-9 text-muted-foreground" />
        <h1 className="mt-3 text-xl font-semibold text-foreground">Email Tracking</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {error ?? "No tracking report is available yet."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Email Tracking</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Consent-aware open and click tracking with persisted engagement profiles and deliverability context.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard icon={<Eye className="h-4 w-4" />} label="Open Rate" value={formatPct(data.summary.openRatePct)} sub={`${data.summary.openEvents30d} open events`} />
        <MetricCard icon={<MousePointerClick className="h-4 w-4" />} label="Click Rate" value={formatPct(data.summary.clickRatePct)} sub={`${data.summary.clickEvents30d} click events`} />
        <MetricCard icon={<UserRoundCheck className="h-4 w-4" />} label="Engagement Profiles" value={String(data.summary.engagementProfiles)} sub="Persisted CRM profile rows" />
        <MetricCard icon={<TrendingUp className="h-4 w-4" />} label="Reputation" value={data.deliverability.reputationScore == null ? "N/A" : String(Math.round(data.deliverability.reputationScore))} sub={`${formatPct(data.deliverability.bounceRatePct)} bounce`} />
        <MetricCard icon={<ShieldCheck className="h-4 w-4" />} label="Consent Mode" value={data.consent.failClosed ? "Fail-closed" : "Tracked"} sub="KV opt-out checked" />
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <ShieldCheck className="h-4 w-4 text-[var(--color-success)]" />
          Consent-Aware Collection
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{data.consent.source}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <SmallStat label="Sent 30d" value={data.summary.sent30d.toLocaleString()} />
          <SmallStat label="Unsubscribes 30d" value={data.summary.unsubscribed30d.toLocaleString()} />
          <SmallStat label="Complaint rate" value={formatPct(data.deliverability.complaintRatePct)} />
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Engagement Profiles</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Rolling 30-day profile counters used by CRM scoring and email segmentation.
            </p>
          </div>
          <span className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground">
            Top {data.profiles.length}
          </span>
        </div>
        {data.profiles.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border bg-background p-5 text-sm text-muted-foreground">
            No engagement profiles yet. Profiles appear after recipients open or click tracked emails.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Person</th>
                  <th className="px-4 py-3">Sent</th>
                  <th className="px-4 py-3">Opened</th>
                  <th className="px-4 py-3">Clicked</th>
                  <th className="px-4 py-3">Open Rate</th>
                  <th className="px-4 py-3">Click Rate</th>
                  <th className="px-4 py-3">Last Click</th>
                  <th className="px-4 py-3">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.profiles.map((profile) => (
                  <tr key={profile.personId} className="stagger-item">
                    <td className="max-w-[220px] truncate px-4 py-3 font-mono text-xs text-foreground">{profile.personId}</td>
                    <td className="px-4 py-3 text-muted-foreground">{profile.emailsSent30d}</td>
                    <td className="px-4 py-3 text-muted-foreground">{profile.emailsOpened30d}</td>
                    <td className="px-4 py-3 text-muted-foreground">{profile.emailsClicked30d}</td>
                    <td className="px-4 py-3 text-[var(--color-success)]">{formatPct(profile.openRatePct)}</td>
                    <td className="px-4 py-3 text-[var(--color-primary)]">{formatPct(profile.clickRatePct)}</td>
                    <td className="px-4 py-3 text-muted-foreground">{formatDate(profile.lastEmailClickedAt)}</td>
                    <td className="px-4 py-3 font-semibold text-foreground">{profile.engagementScore}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ icon, label, value, sub }: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="stagger-item rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
        <div className="text-muted-foreground">{icon}</div>
      </div>
      <p className="mt-2 text-2xl font-bold text-foreground">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}

function SmallStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stagger-item rounded-lg border border-border bg-background px-4 py-3">
      <p className="text-xs uppercase text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold text-foreground">{value}</p>
    </div>
  );
}
